import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { House } from './House';
import { Terrain, Clouds } from './Terrain';
import { Plants } from './Plants';

// Shared progress (avoids React re-renders every frame)
const progressRef = { current: 0 };

// SkyDome — inside of a large sphere with a warm golden-hour gradient.
// One-time cost. Adds cinematic feel without env-map sampling overhead.
const SkyDome = () => {
    const geom = useMemo(() => new THREE.SphereGeometry(200, 32, 24), []);
    const mat = useMemo(() => {
        const size = 512;
        const c = document.createElement('canvas');
        c.width = 16; c.height = size;
        const ctx = c.getContext('2d');
        const g = ctx.createLinearGradient(0, 0, 0, size);
        g.addColorStop(0.00, '#0f1a2a');       // deep blue at zenith
        g.addColorStop(0.30, '#3a4560');       // dusky blue
        g.addColorStop(0.55, '#c76b3f');       // orange band
        g.addColorStop(0.75, '#e9a967');       // warm gold
        g.addColorStop(0.90, '#f2d09a');       // hazy horizon
        g.addColorStop(1.00, '#a06642');       // ground haze
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, 16, size);
        const tex = new THREE.CanvasTexture(c);
        tex.wrapS = THREE.RepeatWrapping;
        tex.wrapT = THREE.ClampToEdgeWrapping;
        tex.needsUpdate = true;
        return new THREE.MeshBasicMaterial({ map: tex, side: THREE.BackSide, depthWrite: false, fog: false });
    }, []);
    return <mesh geometry={geom} material={mat} />;
};

const SceneDriver = ({ mouseRef }) => {
    const { camera, invalidate } = useThree();
    const target = useRef(new THREE.Vector3(0, 1.2, 0));
    const lastP = useRef(-1);

    useFrame(() => {
        const p = window.__scrollProgress || 0;
        progressRef.current = p;

        let radius, angle, height, look;
        if (p < 0.55) {
            const t = p / 0.55;
            angle = -Math.PI * 0.15 + t * Math.PI * 0.7;
            radius = 14 - t * 3;
            height = 3.2 - t * 0.6;
            look = new THREE.Vector3(0, 1.4, 0);
        } else if (p < 0.72) {
            const t = (p - 0.55) / 0.17;
            angle = Math.PI * 0.55 - Math.PI * 0.55 * t;
            radius = THREE.MathUtils.lerp(11, 4.5, t);
            height = THREE.MathUtils.lerp(2.6, 1.6, t);
            look = new THREE.Vector3(0, 1.4, 0);
        } else {
            const t = (p - 0.72) / 0.28;
            angle = 0;
            radius = THREE.MathUtils.lerp(4.5, -3.5, t);
            height = 1.5;
            const roomX = Math.sin(t * Math.PI * 2) * 2.0;
            look = new THREE.Vector3(roomX, 1.3, -2 - t * 4);
        }

        const px = Math.sin(angle) * radius;
        const pz = Math.cos(angle) * radius;
        const m = mouseRef.current;
        const targetPos = new THREE.Vector3(px + m.x * 0.35, height + m.y * 0.12, pz);

        camera.position.lerp(targetPos, 0.055);
        target.current.lerp(look, 0.075);
        camera.lookAt(target.current);
        lastP.current = p;
    });
    return null;
};

// Bridge — passes progress + drives cloud drift (single useFrame for whole scene)
const SceneContent = () => {
    const houseRef = useRef();
    const plantsRef = useRef();
    const interiorRef = useRef();
    const cloudRef = useRef();

    useFrame(({ clock }) => {
        const p = progressRef.current;
        if (houseRef.current) houseRef.current.setProgress(p);
        if (plantsRef.current) plantsRef.current.setProgress(p);
        if (interiorRef.current) {
            const t = THREE.MathUtils.clamp((p - 0.68) / 0.05, 0, 1);
            interiorRef.current.traverse((c) => {
                if (c.material && 'opacity' in c.material) c.material.opacity = t;
            });
        }
        if (cloudRef.current) cloudRef.current.position.x = Math.sin(clock.elapsedTime * 0.012) * 8;
    });

    return (
        <>
            <Terrain />
            <group ref={cloudRef} position={[0, 20, -75]}>
                <Clouds />
            </group>
            <House ref={houseRef} />
            <Plants ref={plantsRef} />
            <group ref={interiorRef} name="interior" position={[0, 0.02, -1.5]}>
                <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                    <planeGeometry args={[5.8, 3.8]} />
                    <meshLambertMaterial color="#c9b48a" transparent opacity={0} />
                </mesh>
                <mesh position={[0, 1.2, -1.9]}>
                    <boxGeometry args={[5.6, 2.4, 0.08]} />
                    <meshLambertMaterial color="#F1EBDD" transparent opacity={0} />
                </mesh>
                <mesh position={[-1.8, 0.5, -0.6]}>
                    <boxGeometry args={[1.3, 1.0, 0.7]} />
                    <meshLambertMaterial color="#F4F0EA" transparent opacity={0} />
                </mesh>
                <mesh position={[1.5, 0.4, -0.4]}>
                    <boxGeometry args={[1.6, 0.8, 0.9]} />
                    <meshLambertMaterial color="#6b4a2a" transparent opacity={0} />
                </mesh>
                <pointLight position={[0, 2, -1]} color="#E8AC65" intensity={1.2} distance={5} />
            </group>
        </>
    );
};

// Cinematic lighting — no HDRI, tuned directional + hemi
const Sun = () => (
    <>
        <ambientLight intensity={0.42} color="#f4d6a8" />
        <hemisphereLight args={['#f7cc95', '#3a2b1e', 0.55]} />
        <directionalLight
            position={[-14, 12, 9]}
            intensity={3.2}
            color="#FFCB8A"
            castShadow
            shadow-mapSize-width={1024}
            shadow-mapSize-height={1024}
            shadow-camera-near={0.5}
            shadow-camera-far={55}
            shadow-camera-left={-16}
            shadow-camera-right={16}
            shadow-camera-top={16}
            shadow-camera-bottom={-16}
            shadow-bias={-0.0004}
            shadow-normalBias={0.03}
        />
        {/* Cool fill from sky opposite */}
        <directionalLight position={[10, 6, -8]} intensity={0.4} color="#7BA2C7" />
        {/* Warm rim */}
        <directionalLight position={[-4, 4, -10]} intensity={0.7} color="#E88569" />
        {/* Ground bounce (warm) */}
        <directionalLight position={[0, -2, 3]} intensity={0.22} color="#c78256" />
    </>
);

// Ground shadow substitute for ContactShadows — cheap darkening plane
const ShadowPlate = () => (
    <mesh position={[0, 0.011, 0.5]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[16, 9]} />
        <meshBasicMaterial color="#1a0f08" transparent opacity={0.55} />
    </mesh>
);

// Auto-pause frameloop when hero is offscreen (huge FPS win)
const useSmartFrameloop = () => {
    const [frameloop, setFrameloop] = useState('always');
    useEffect(() => {
        let last = 'always';
        const onScroll = () => {
            const p = window.__scrollProgress || 0;
            const next = p < 0.18 ? 'always' : 'never';
            if (next !== last) { last = next; setFrameloop(next); }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);
    return frameloop;
};

export const Experience = () => {
    const mouseRef = useRef({ x: 0, y: 0 });
    const frameloop = useSmartFrameloop();

    useEffect(() => {
        const onMove = (e) => {
            mouseRef.current.x = (e.clientX / window.innerWidth - 0.5) * 2;
            mouseRef.current.y = -(e.clientY / window.innerHeight - 0.5) * 2;
        };
        window.addEventListener('mousemove', onMove, { passive: true });
        return () => window.removeEventListener('mousemove', onMove);
    }, []);

    return (
        <div className="fixed inset-0 z-0" data-testid="msd-3d-canvas">
            <Canvas
                shadows
                dpr={1}
                frameloop={frameloop}
                camera={{ fov: 40, position: [10, 3, 10], near: 0.1, far: 300 }}
                gl={{ antialias: false, powerPreference: 'high-performance', alpha: false, stencil: false, depth: true }}
                onCreated={({ gl, scene }) => {
                    gl.toneMapping = THREE.ACESFilmicToneMapping;
                    gl.toneMappingExposure = 1.15;
                    gl.shadowMap.type = THREE.PCFSoftShadowMap;
                    scene.background = new THREE.Color('#e6b98a');
                    scene.fog = new THREE.Fog('#d99f6c', 42, 165);
                }}
            >
                <color attach="background" args={['#e6b98a']} />
                <fog attach="fog" args={['#d99f6c', 42, 165]} />

                <Sun />
                <Suspense fallback={null}>
                    <SkyDome />
                    <SceneContent />
                    <ShadowPlate />
                </Suspense>

                <SceneDriver mouseRef={mouseRef} />
            </Canvas>

            {/* Cinematic gradient overlays for legibility */}
            <div
                className="pointer-events-none absolute inset-0 z-10"
                style={{
                    background:
                        'linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0) 24%, rgba(10,10,10,0) 58%, rgba(10,10,10,0.9) 100%)',
                }}
            />
            <div
                className="pointer-events-none absolute inset-0 z-10"
                style={{
                    background:
                        'radial-gradient(ellipse at 30% 60%, rgba(0,0,0,0) 30%, rgba(0,0,0,0.35) 100%)',
                }}
            />
        </div>
    );
};
